/**
 * 企业报告 Playwright 印刷模式页面（spec §4.2）。
 *
 * 路由：/print/enterprise/:id?token=<hmac>（不嵌 PrivateRoute，HMAC token 鉴权）。
 *  - id='preview' / 'demo' → 本地 mock，无需 token（本地调样式）。
 *  - 真 id → GET /api/enterprises/<id>/report/print-data/?token=xxx。
 *  - 渲染完后双 rAF + 300ms 置 data-report-ready=1，Playwright 同步。
 */
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import EnterpriseReport from '../components/report_enterprise/EnterpriseReport';
import { mockEnterpriseReportPayload } from '../mocks/enterpriseReportMock';

// 原生 fetch（绕 api/client.js 拦截器）：印刷模式只认 HMAC token。
async function fetchPrintData(enterpriseId, token) {
  const url = `/api/enterprises/${encodeURIComponent(enterpriseId)}/report/print-data/`
    + `?token=${encodeURIComponent(token)}`;
  const resp = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    credentials: 'omit',
  });
  let body = null;
  try { body = await resp.json(); } catch (_) { body = null; }
  return { ok: resp.ok, status: resp.status, body };
}

export default function PrintEnterpriseReportPage() {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const id = params.id || 'preview';
  const token = searchParams.get('token') || '';

  const [payload, setPayload] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (id === 'preview' || id === 'demo') {
        await new Promise((r) => setTimeout(r, 50));
        if (!cancelled) setPayload(mockEnterpriseReportPayload);
        return;
      }
      if (!token) {
        setErrorMsg('缺少 token 参数。印刷模式 URL 形如 /print/enterprise/<id>?token=<hmac>。');
        return;
      }
      const { ok, status, body } = await fetchPrintData(id, token);
      if (cancelled) return;
      if (!ok) {
        setErrorMsg(`拉 print-data 失败 (${status}): ${body?.detail || `HTTP ${status}`}`);
        return;
      }
      setPayload(body);
    }
    load();
    return () => { cancelled = true; };
  }, [id, token]);

  useEffect(() => {
    if (!payload) return undefined;
    let raf1 = 0;
    let raf2 = 0;
    let timer = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        timer = window.setTimeout(() => {
          document.documentElement.dataset.reportReady = '1';
          window.__reportReady = true;
        }, 300);
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      if (timer) window.clearTimeout(timer);
      delete document.documentElement.dataset.reportReady;
      window.__reportReady = false;
    };
  }, [payload]);

  if (errorMsg) {
    return (
      <div style={{ padding: 40, fontFamily: 'sans-serif' }}>
        <h2>印刷模式暂未就绪</h2>
        <p>{errorMsg}</p>
      </div>
    );
  }
  if (!payload) {
    return <div style={{ padding: 40, fontFamily: 'sans-serif' }}>正在加载企业报告…</div>;
  }
  return <EnterpriseReport payload={payload} printMode />;
}
